import {
  ActionIcon,
  Box,
  Checkbox,
  Group,
  Input,
  Menu,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import { useDisclosure, useFocusWithin } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import { IconCircleMinus, IconMoodHappy } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MESSAGE_SIGNIN_ICON_ARRAY, useCanAccess } from '@mygpt/app';

import { SvgLightWarning } from '@/components/Common/icon';
import { useWidgetFormContext } from '@/components/Widget/Embed/form-context';
import { useWidgetForm } from '@/hooks/useWidgetForm';

import { ModalSignatureMessagesTips } from '../Modal';
import { SettingCard } from './SettingCard';

const MAX_SIGNATURE_MESSAGES_ITEM_LENGTH = 200;

/** 品牌与署名：页脚品牌 + 签名信息（共用） */
export const GeneralBrand = () => {
  const { t } = useTranslation();
  const { form } = useWidgetFormContext();
  const { onOverrideChangeForForm } = useWidgetForm();
  const canAccessWidgetCustomization = useCanAccess('enable_widget_customization');

  const [openedSignature, { close: closeSignature, open: openSignature }] = useDisclosure(false);
  const { focused, ref: focusRef } = useFocusWithin();
  const [menuOpened, setMenuOpened] = useState(false);

  const addSignatureMessagesList = (index: number) => {
    setMenuOpened(false);
    if (form.values.chatbot.signatureMessages.list.length >= 3) {
      showNotification({
        message: t('widget.setting.chatbot.signature_messages_limit'),
        color: 'red',
      });
      return;
    }
    form.setFieldValue('chatbot.signatureMessages.list', [
      ...form.values.chatbot.signatureMessages.list,
      {
        iconIndex: index,
        value: '',
      },
    ]);
  };

  const delSignatureMessagesListItem = (index: number) => {
    const list = form.values.chatbot.signatureMessages.list.concat([]);
    list.splice(index, 1);
    form.setFieldValue('chatbot.signatureMessages.list', list);

    // 模仿触发了 blur 时的保存效果，触发自动保存逻辑
    onOverrideChangeForForm('')({ type: 'blur', isTrusted: true });
  };

  const MESSAGE_SIGNIN_ICON_TEXT_ARRAY = [
    t('widget.setting.chatbot.icon_email'),
    t('widget.setting.chatbot.icon_phone'),
    t('widget.setting.chatbot.icon_location'),
    t('widget.setting.chatbot.icon_fax'),
    t('widget.setting.chatbot.icon_link'),
    t('widget.setting.chatbot.icon_date'),
    t('widget.setting.chatbot.icon_time'),
    t('widget.setting.chatbot.icon_text'),
  ];

  return (
    <SettingCard title={t('widget.setting.groups.card_brand')}>
      <Stack gap={20}>
        <Group justify="space-between">
          <Checkbox
            label={t('widget.setting.chatbot.footer_brand_label')}
            disabled={!canAccessWidgetCustomization}
            checked={form.values.chatbot.footerBrand.checked}
            {...form.getInputProps('chatbot.footerBrand.checked')}
            onChange={onOverrideChangeForForm('chatbot.footerBrand.checked')}
          />
          <section className="w-[200px]">
            <Input
              autoComplete="off"
              disabled={!canAccessWidgetCustomization}
              rightSection={
                <Tooltip label={t('widget.setting.chatbot.footer_brand_tip')} multiline w={200}>
                  <div>
                    <SvgLightWarning />
                  </div>
                </Tooltip>
              }
              {...form.getInputProps('chatbot.footerBrand.value')}
              onBlur={onOverrideChangeForForm('chatbot.footerBrand.value')}
            />
          </section>
        </Group>

        <Stack>
          <Checkbox
            label={<Text>{t('widget.setting.chatbot.signature_messages_label')}</Text>}
            disabled={!canAccessWidgetCustomization}
            checked={form.values.chatbot.signatureMessages.checked}
            {...form.getInputProps('chatbot.signatureMessages.checked')}
            onChange={onOverrideChangeForForm('chatbot.signatureMessages.checked')}
          />
          <section>
            <Text size="sm" c="gray.6" inline>
              {t('widget.setting.chatbot.signature_messages_tip')}
              <UnstyledButton onClick={openSignature}>
                <Text span c={'violet'} size="sm" ml={5}>
                  {t('create_v2.read_more')}
                </Text>
              </UnstyledButton>
            </Text>
          </section>
          <Box
            ref={focusRef}
            className={`bg-white border rounded-lg overflow-hidden ${
              !form.values.chatbot.signatureMessages.checked && 'opacity-[.6]'
            } ${focused ? 'border-[#5A4CB7]' : 'border-[#E4E8FF]'}`}
          >
            <Stack gap={5}>
              <section className="py-[10px] px-[12px]">
                <section className="flex items-center gap-3">
                  <TextInput
                    disabled={!form.values.chatbot.signatureMessages.checked}
                    className="flex-auto"
                    variant="unstyled"
                    autoComplete="off"
                    placeholder={t('widget.setting.chatbot.signature_messages_value_placeholder')}
                    {...form.getInputProps('chatbot.signatureMessages.value')}
                    onBlur={onOverrideChangeForForm('chatbot.signatureMessages.value')}
                  />
                  <Menu shadow="md" opened={menuOpened} onChange={setMenuOpened}>
                    <Menu.Target>
                      <Tooltip label={t('widget.setting.chatbot.signature_messages_limit')}>
                        <ActionIcon
                          variant="subtle"
                          disabled={!form.values.chatbot.signatureMessages.checked}
                          className="flex-none"
                        >
                          <IconMoodHappy />
                        </ActionIcon>
                      </Tooltip>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Box p={5}>
                        <SimpleGrid cols={5} spacing="sm" verticalSpacing="sm">
                          {MESSAGE_SIGNIN_ICON_ARRAY.map((ItemIcon, index) => {
                            return (
                              <Tooltip key={index} label={MESSAGE_SIGNIN_ICON_TEXT_ARRAY[index]} withArrow>
                                <UnstyledButton
                                  key={index}
                                  onClick={() => {
                                    addSignatureMessagesList(index);
                                  }}
                                >
                                  <section className="p-1">
                                    <ItemIcon />
                                  </section>
                                </UnstyledButton>
                              </Tooltip>
                            );
                          })}
                        </SimpleGrid>
                      </Box>
                    </Menu.Dropdown>
                  </Menu>
                </section>
                {!!form.values.chatbot.signatureMessages.list.length && (
                  <Stack gap={10} className="border-t border-dashed border-[#E4E8FF] pt-5 mb-3">
                    {form.values.chatbot.signatureMessages.list.map((item, index) => {
                      const ItemIcon = MESSAGE_SIGNIN_ICON_ARRAY[item.iconIndex];
                      return (
                        <TextInput
                          data-autofocus
                          key={index}
                          disabled={!form.values.chatbot.signatureMessages.checked}
                          placeholder={t('widget.setting.chatbot.signature_messages_item_placeholder', {
                            type: MESSAGE_SIGNIN_ICON_TEXT_ARRAY[item.iconIndex].toLocaleLowerCase(),
                          })}
                          autoComplete="off"
                          maxLength={MAX_SIGNATURE_MESSAGES_ITEM_LENGTH}
                          leftSection={
                            <section className="p-1">
                              <ItemIcon />
                            </section>
                          }
                          rightSectionWidth={110}
                          rightSection={
                            <Group gap={10} justify="flex-end" w={100} wrap="nowrap">
                              <Text c="gray.3" size="sm">
                                {form.values.chatbot.signatureMessages.list[index].value.length}/
                                {MAX_SIGNATURE_MESSAGES_ITEM_LENGTH}
                              </Text>
                              <ActionIcon
                                variant="subtle"
                                color="red"
                                disabled={!form.values.chatbot.signatureMessages.checked}
                                onClick={() => {
                                  delSignatureMessagesListItem(index);
                                }}
                              >
                                <IconCircleMinus color="red" size="1.125rem" />
                              </ActionIcon>
                            </Group>
                          }
                          {...form.getInputProps(`chatbot.signatureMessages.list.${index}.value`)}
                          onBlur={onOverrideChangeForForm(`chatbot.signatureMessages.list.${index}.value`)}
                        />
                      );
                    })}
                  </Stack>
                )}
              </section>
            </Stack>
          </Box>
        </Stack>
      </Stack>

      <ModalSignatureMessagesTips opened={openedSignature} onClose={closeSignature} size={'xl'} centered />
    </SettingCard>
  );
};
